import { supabase } from '../lib/supabase';

export interface ComparisonStats {
  averageScore: number;
  userPercentile: number;
  totalDiagnostics: number;
  averageByHousingType: {
    house: number;
    apartment: number;
  };
}

export async function getAnonymousComparison(
  userScore: number,
  housingType: 'house' | 'apartment'
): Promise<ComparisonStats> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    return {
      averageScore: userScore,
      userPercentile: 50,
      totalDiagnostics: 1,
      averageByHousingType: { house: userScore, apartment: userScore },
    };
  }

  const { data, error } = await supabase.rpc('get_anonymous_comparison', {
    p_user_id: user.id,
    p_housing_type: housingType,
  });

  if (error || !data) {
    console.error('Error calling get_anonymous_comparison RPC:', error?.message);
    return {
      averageScore: userScore,
      userPercentile: 50,
      totalDiagnostics: 1,
      averageByHousingType: { house: userScore, apartment: userScore },
    };
  }

  const avgByType = data.averageScoreByType ?? data.averageScore;

  return {
    averageScore: data.averageScore,
    userPercentile: data.userPercentile,
    totalDiagnostics: data.totalDiagnostics,
    averageByHousingType: {
      house: housingType === 'house' ? avgByType : data.averageScore,
      apartment: housingType === 'apartment' ? avgByType : data.averageScore,
    },
  };
}

export async function getScoreDistribution(): Promise<{ range: string; count: number }[]> {
  const { data: diagnosticsData, error: diagnosticsError } = await supabase
    .from('diagnostics')
    .select('id, security_score');

  if (diagnosticsError || !diagnosticsData) {
    return [];
  }

  const { data: latestSnapshots, error: snapshotsError } = await supabase
    .from('score_snapshots')
    .select('diagnostic_id, score')
    .in('diagnostic_id', diagnosticsData.map(d => d.id))
    .order('created_at', { ascending: false });

  if (snapshotsError) {
    console.error('Error fetching snapshots for distribution:', snapshotsError);
  }

  const latestSnapshotMap = new Map<string, number>();
  if (latestSnapshots) {
    for (const snapshot of latestSnapshots) {
      if (!latestSnapshotMap.has(snapshot.diagnostic_id)) {
        latestSnapshotMap.set(snapshot.diagnostic_id, snapshot.score);
      }
    }
  }

  const currentScores = diagnosticsData.map(d =>
    latestSnapshotMap.get(d.id) || d.security_score || 0
  );

  const distribution = [
    { range: '0-20', count: 0 },
    { range: '21-40', count: 0 },
    { range: '41-60', count: 0 },
    { range: '61-80', count: 0 },
    { range: '81-100', count: 0 }
  ];

  currentScores.forEach((score) => {
    if (score <= 20) distribution[0].count++;
    else if (score <= 40) distribution[1].count++;
    else if (score <= 60) distribution[2].count++;
    else if (score <= 80) distribution[3].count++;
    else distribution[4].count++;
  });

  return distribution;
}
